export type MemberDeviceStatus = 'active' | 'revoked' | 'transferred';

export interface MemberDeviceRow {
  authorizationId: number;
  username: string;
  deviceId: string;
  linkedAt: string;
  lastSeenAt: string | null;
  status: MemberDeviceStatus;
  revokedReason: string | null;
  revokedAt: string | null;
}

type RevokeResponseData = {
  authorizationId?: unknown;
  status?: unknown;
  revokedReason?: unknown;
  revokedAt?: unknown;
  alreadyRevoked?: unknown;
};

export type RevokeResponse = {
  code?: unknown;
  data?: RevokeResponseData | null;
};

/** Keeps an old revoke completion from changing a newer modal operation. */
export class MemberDeviceRevokeOperationGuard {
  private generation = 0;

  begin(): number {
    this.generation += 1;
    return this.generation;
  }

  invalidate(): void {
    this.generation += 1;
  }

  isCurrent(operation: number): boolean {
    return operation === this.generation;
  }
}

export function canBeginMemberDeviceRevoke(revokeLoadingId: number | null): boolean {
  return revokeLoadingId === null;
}

export function canRevokeMemberDevice(row: MemberDeviceRow, isAllScope: boolean): boolean {
  return !isAllScope && row.status === 'active';
}

export function canOpenMemberDeviceRevoke(
  row: MemberDeviceRow,
  isAllScope: boolean,
  isLookupLoading: boolean,
): boolean {
  return !isLookupLoading && canRevokeMemberDevice(row, isAllScope);
}

export function canConfirmMemberDeviceRevoke(
  selection: MemberDeviceRow | null,
  revokeLoadingId: number | null,
): boolean {
  return selection !== null && canBeginMemberDeviceRevoke(revokeLoadingId);
}

export function isCurrentRevokeScope(capturedBrand: string, currentScope: string): boolean {
  return currentScope !== 'all' && capturedBrand === currentScope;
}

export function memberDeviceRevokeRequest(authorizationId: number, targetBrand: string) {
  return {
    url: `/api/admin/member-device-authorizations/${authorizationId}/revoke`,
    init: {
      method: 'POST',
      body: JSON.stringify({ targetBrand, reason: 'admin_revoked' }),
    },
  };
}

/** Returns null without changing displayed rows for a malformed or stale response. */
export function reconcileMemberDeviceRevoke(
  rows: MemberDeviceRow[],
  response: RevokeResponse,
  authorizationId: number,
): MemberDeviceRow[] | null {
  const data = response.data;
  if (
    response.code !== 0 || !data || data.authorizationId !== authorizationId ||
    data.status !== 'revoked' ||
    (data.revokedReason !== null && typeof data.revokedReason !== 'string') ||
    (data.revokedAt !== null && typeof data.revokedAt !== 'string') ||
    !rows.some(row => row.authorizationId === authorizationId && row.status === 'active')
  ) return null;

  return rows.map(row => row.authorizationId === authorizationId ? {
    ...row,
    status: 'revoked',
    revokedReason: data.revokedReason as string | null,
    revokedAt: data.revokedAt as string | null,
  } : row);
}

export function isAlreadyRevokedResponse(response: RevokeResponse): boolean {
  return response.data?.alreadyRevoked === true;
}
